// Typed client for POST /api/medications/barcode.
// Mirrors the server-side BarcodeHit shape but exposes only the bits
// the candidate picker uses.

import { useCallback } from 'react';
import { env } from '../env';
import { useSession } from '../hooks/SessionProvider';
import { UnauthenticatedError } from '../hooks/useAuthedFetch';
import type { OcrCandidate, OcrDosingHint, OcrFdaLabel } from './ocr';

export interface BarcodeResolveResult {
  resolved: boolean;
  code: string;
  kind?: 'ndc' | 'upc' | 'gtin' | 'unknown';
  source?: string;
  candidates: OcrCandidate[];
  fdaLabel?: OcrFdaLabel | null;
  dosingHint?: OcrDosingHint | null;
}

interface ApiSuccess<T> {
  ok: true;
  data: T;
}
interface ApiError {
  ok: false;
  error: { code: string; message: string };
}

export function useBarcodeResolve(): (code: string) => Promise<BarcodeResolveResult> {
  const { sessionToken, refresh } = useSession();
  return useCallback(
    async (code: string): Promise<BarcodeResolveResult> => {
      const send = async (token: string | null): Promise<Response> => {
        if (!token) throw new UnauthenticatedError();
        return fetch(`${env.apiBaseUrl}/api/medications/barcode`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });
      };
      let token = sessionToken;
      if (!token) token = await refresh();
      let res = await send(token);
      if (res.status === 401) {
        const fresh = await refresh();
        if (!fresh) throw new UnauthenticatedError();
        res = await send(fresh);
      }
      const body = (await res.json()) as ApiSuccess<BarcodeResolveResult> | ApiError;
      if (!body.ok) throw new Error(`${body.error.code}: ${body.error.message}`);
      return body.data;
    },
    [sessionToken, refresh],
  );
}
